import {
  BREADCRUMBS_POSITION_OPTIONS,
  CONTAINER_OPTIONS,
  CONTENT_LAYOUT_OPTIONS,
  HEADING_ALIGNMENT_OPTIONS,
  IMAGE_ASPECT_RATIO_OPTIONS,
  MODULE_FILL_TYPE_OPTIONS,
  TITLE_SIZE_OPTIONS,
  TITLE_TYPE_OPTIONS,
} from '@/common/constants';
import { useModuleStyles } from '@/common/hooks/useModuleStyles';
import type { CTAType } from '@/common/types';
import { HeadingBlock, HeadingBlockProps } from '@/components/blocks/HeadingBlock';
import { ImageBlock } from '@/components/blocks/ImageBlock';
import { Breadcrumbs, BreadcrumbsProps } from '@/components/Breadcrumbs/Breadcrumbs';
import classNames from 'classnames';
import React from 'react';
import styles from './FeatureAnnouncementHero.module.scss';
import type { MediaItem } from './MediaCarousel';
import { MediaCarousel } from './MediaCarousel';

export interface FeatureAnnouncementHeroProps {
  title: string;
  subtitle?: string;
  counterText?: string;
  image?: { src: string; alt: string };
  fallbackMedia?: MediaItem[];
  cta?: { label: string; link: string; type: string };
  breadcrumbs?: BreadcrumbsProps;
  isFirstModule?: boolean;
}

export const FeatureAnnouncementHero: React.FC<FeatureAnnouncementHeroProps> = ({
  title,
  subtitle,
  counterText,
  image,
  fallbackMedia = [],
  cta,
  breadcrumbs,
  isFirstModule,
}) => {
  const hasCustomImage = !!image;
  const hasFallbackMedia = fallbackMedia.length > 0;
  const showBreadcrumbs =
    isFirstModule && breadcrumbs && breadcrumbs.position === BREADCRUMBS_POSITION_OPTIONS.PAGE_TOP;

  const headingConfig: HeadingBlockProps = {
    title,
    subheading: counterText,
    titleSize: TITLE_SIZE_OPTIONS.LARGE,
    titleType: TITLE_TYPE_OPTIONS.H1,
    bodyRichTextAsHTML: subtitle,
    alignment: HEADING_ALIGNMENT_OPTIONS.START,
    ctas: cta ? [{ label: cta.label, link: cta.link, type: cta.type as CTAType }] : undefined,
  };

  const { sectionStyles, moduleBaseStyles } = useModuleStyles({
    container: CONTAINER_OPTIONS.CONTAINED,
    fillType: MODULE_FILL_TYPE_OPTIONS.BASE,
    layout: CONTENT_LAYOUT_OPTIONS.CONTENT_TRAILING,
    moduleClassName: 'module-feature-announcement-hero',
    styles,
    isFirstModule,
  });

  return (
    <section className={classNames(sectionStyles, showBreadcrumbs && styles.with_breadcrumb)}>
      {showBreadcrumbs && <Breadcrumbs {...breadcrumbs} className={styles.module_breadcrumbs} />}
      <div
        className={classNames(
          moduleBaseStyles,
          styles.hero_container,
          hasCustomImage && styles['hero--custom'],
        )}
      >
        <div className={styles.hero_content}>
          {hasCustomImage && image.src && (
            <div className={classNames(styles.hero__image, styles['hero__image--landscape'])}>
              <ImageBlock
                src={image.src}
                alt={image.alt}
                aspectRatio={IMAGE_ASPECT_RATIO_OPTIONS.LANDSCAPE}
                isLazyLoad={false}
              />
            </div>
          )}
          {!hasCustomImage && hasFallbackMedia && (
            <div className={styles.hero__image}>
              <MediaCarousel items={fallbackMedia} />
            </div>
          )}
          <div className={styles.hero__text}>
            <HeadingBlock {...headingConfig} className={styles['hero-heading-block']} />
          </div>
        </div>
      </div>
    </section>
  );
};
